import { ReactElement, useCallback, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  Stack,
  DialogContentText,
  TextField,
  Alert,
  Link,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikHelpers, FormikProvider } from 'formik';
import { useSnackbar } from 'notistack';

import { useLazyRequest } from '~hooks/useLazyRequest';
import { rebillItems } from '~services/billingItem';
import { hasError } from '~utils/formHelpers';
import { RebillMode } from '~globals/types/enums';

import { DialogRebillOrdersProps, DialogRebilOrdersMode } from './types';
import {
  FIELDS_NAME,
  getInitialValues,
  validationSchema,
  isAutomaticMode,
} from './utils';
import DialogRebillOrdersCardMode from './CardMode';
import {
  DialogRebillOrdersContainer,
  DialogRebillOrdersActions,
} from './styles';

export const DialogRebillOrders = ({
  open,
  onClose,
  itemIds,
  excludeMode,
  onSuccessSubmit,
}: DialogRebillOrdersProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingRebillItems, , exceuteRebillItems] = useLazyRequest({
    request: rebillItems,
    withPostSuccess: () => {
      enqueueSnackbar('Pedidos retarifados correctamente', {
        variant: 'success',
      });

      onClose();

      onSuccessSubmit?.();
    },
    withPostFailure: () => {
      enqueueSnackbar(
        'No se pudieron retarifar los pedidos, intenté nuevamente',
        { variant: 'error' },
      );
    },
  });

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const isAutomatic = isAutomaticMode(
        values[FIELDS_NAME.MODE] as DialogRebilOrdersMode,
      );

      await exceuteRebillItems({
        rebillMode: isAutomatic ? RebillMode.Automatic : RebillMode.Manual,
        shipPrice: isAutomatic ? 0 : Number(values[FIELDS_NAME.AMOUNT]),
        itemIds,
      });

      setSubmitting(false);
    },
    [exceuteRebillItems, itemIds],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(excludeMode),
    validationSchema,
    onSubmit: onSubmit,
  });

  const {
    values,
    errors,
    touched,
    getFieldProps,
    submitForm,
    isSubmitting,
    resetForm,
  } = formikBag;

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DialogRebillOrdersContainer open={open}>
      <DialogTitle>Retarifar pedidos</DialogTitle>

      <FormikProvider value={formikBag}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText color="text.secondary">
              Seleccione la opción deseada para retarifar los pedidos
              seleccionados.
            </DialogContentText>

            <Stack spacing={1}>
              {excludeMode !== 'automatic' && (
                <DialogRebillOrdersCardMode
                  mode="automatic"
                  title="Tarifa automática"
                  // eslint-disable-next-line max-len
                  description="Se calculará el importe de los pedidos según la lista de precios correspondiente."
                  isRecommeded
                />
              )}

              {excludeMode !== 'manual' && (
                <DialogRebillOrdersCardMode
                  mode="manual"
                  title="Tarifa manual"
                  // eslint-disable-next-line max-len
                  description="Puede definir manualmente un importe para los pedidos seleccionados."
                >
                  <TextField
                    label="Nuevo importe"
                    autoComplete="off"
                    type="number"
                    fullWidth
                    error={hasError(touched, errors, FIELDS_NAME.AMOUNT)}
                    {...getFieldProps(FIELDS_NAME.AMOUNT)}
                    InputLabelProps={{ shrink: true }}
                    disabled={isAutomaticMode(
                      values[FIELDS_NAME.MODE] as DialogRebilOrdersMode,
                    )}
                    sx={{ maxWidth: 250 }}
                  />
                </DialogRebillOrdersCardMode>
              )}
            </Stack>

            <Alert
              severity="info"
              variant="standard"
              icon={false}
              sx={(currentTheme) => ({
                border: `1px solid ${currentTheme.palette.info.light}`,
                ...currentTheme.typography.caption,
              })}
            >
              Solo se tarifarán los pedidos que se encuentren completos. Tampoco
              serán retarifados aquellos pedidos que hayan sido previamente
              liquidados.
            </Alert>

            <DialogContentText variant="caption">
              Para mayor información sobre tarifas y listas de precios, diríjase
              a este{' '}
              <Link
                href="https://routix.tawk.help/article/asignar-lista-de-precios"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                artículo
              </Link>
            </DialogContentText>
          </Stack>
        </DialogContent>
      </FormikProvider>

      <DialogRebillOrdersActions>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingRebillItems || isSubmitting}
        >
          Guardar
        </LoadingButton>
      </DialogRebillOrdersActions>
    </DialogRebillOrdersContainer>
  );
};

export default DialogRebillOrders;
